import {
  CATEGORY_LABEL,
  type ScoreEntity,
  type WhereCategory,
  type WhereField,
  type WhereGroup,
} from '@parcel/filters';
import {
  Button,
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from '@parcel/ui';
import { ChevronDown, ChevronLeft, ChevronRight, Plus, X } from 'lucide-react';
import * as React from 'react';
import type {
  ActionProps,
  CombinatorSelectorProps,
  FieldSelectorProps,
  NotToggleProps,
  OperatorSelectorProps,
  ValueEditorProps,
} from 'react-querybuilder';
import { RuleValueEditor } from '../where-bar/rule-value-editor.js';
import { chipValues } from '../where-bar/where-chip.js';
import type { WhereOptions } from '../where-bar/where-menu.js';

/** What every control reads from the builder's context. */
export interface BuilderContext {
  entity: ScoreEntity;
  fields: readonly WhereField[];
  options: WhereOptions;
  /** Field mode: the card names the field, so the row shows no field picker. */
  lockedField?: string;
  mode: 'field' | 'relation' | 'custom';
  /** How many rules the condition's top level holds. */
  rootRules: number;
  /** The condition, for each row's lead word ("and" / "or"). */
  where: WhereGroup;
  disabled?: boolean;
}

const ctxOf = (context: unknown) => context as BuilderContext;

export type Node = { combinator?: string; rules?: Node[] };

/** "Where" on a group's first rule, the group's own word ("and" / "or") on the next ones. */
function leadWord(path: readonly number[], root: WhereGroup): string {
  if (path[path.length - 1] === 0) return 'Where';
  let group: Node | undefined = root as Node;
  for (const i of path.slice(0, -1)) group = group?.rules?.[i];
  return group?.combinator === 'or' ? 'or' : 'and';
}

/** Two levels: a category, then "Project | City"; joins stay out of sight. */
export function FieldPicker({ value, handleOnChange, context, path }: FieldSelectorProps) {
  const ctx = ctxOf(context);
  const [open, setOpen] = React.useState(false);
  const [category, setCategory] = React.useState<WhereCategory | null>(null);
  if (ctx.lockedField) return null;
  const word = leadWord(path, ctx.where);
  const categories = [...new Set(ctx.fields.map((f) => f.category))];
  const current = ctx.fields.find((f) => f.key === value);
  const choose = (key: string) => {
    handleOnChange(key);
    setOpen(false);
    setCategory(null);
  };
  return (
    <>
      <span className="w-11 shrink-0 text-xs text-muted-foreground">{word}</span>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setCategory(null);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            aria-label="Attribute"
            disabled={ctx.disabled}
            className="h-8 min-w-0 max-w-[240px] gap-1 font-medium"
          >
            <span className="truncate">
              {current
                ? current.relation
                  ? `${CATEGORY_LABEL[current.category]} | ${current.label}`
                  : current.label
                : 'Attribute'}
            </span>
            <ChevronDown className="size-3.5 text-muted-foreground" aria-hidden />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          <Command>
            <CommandList className="max-h-[min(320px,50vh)]">
              {category === null ? (
                <CommandGroup>
                  {categories.map((c) => (
                    <CommandItem key={c} value={`category:${c}`} onSelect={() => setCategory(c)}>
                      <span className="flex-1">{CATEGORY_LABEL[c]}</span>
                      <ChevronRight className="size-3.5 text-muted-foreground" aria-hidden />
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : (
                <CommandGroup>
                  <CommandItem value="back" onSelect={() => setCategory(null)}>
                    <ChevronLeft className="size-3.5 text-muted-foreground" aria-hidden />
                    {CATEGORY_LABEL[category]} fields
                  </CommandItem>
                  {ctx.fields
                    .filter((f) => f.category === category)
                    .map((f) => (
                      <CommandItem
                        key={f.key}
                        value={`field:${f.key}`}
                        onSelect={() => choose(f.key)}
                      >
                        <span className="text-muted-foreground">{CATEGORY_LABEL[f.category]}</span>
                        <span className="text-muted-foreground/60" aria-hidden>
                          |
                        </span>
                        <span className="truncate">{f.label}</span>
                      </CommandItem>
                    ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </>
  );
}

export function OperatorPicker({ options, value, handleOnChange, context }: OperatorSelectorProps) {
  const ctx = ctxOf(context);
  return (
    <select
      aria-label="Condition"
      value={value}
      disabled={ctx.disabled}
      onChange={(e) => handleOnChange(e.target.value)}
      className="h-8 shrink-0 rounded-md border bg-muted/40 px-2 text-sm"
    >
      {(options as ReadonlyArray<{ name: string; label: string }>).map((o) => (
        <option key={o.name} value={o.name}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/** The value as a compact button; the editor opens beside it, as on the tables' chips. */
export function ValueEditorAdapter(props: ValueEditorProps) {
  const ctx = ctxOf(props.context);
  const [open, setOpen] = React.useState(false);
  const field = ctx.fields.find((f) => f.key === props.field);
  if (!field || props.operator === 'null' || props.operator === 'notNull') return null;
  const rule = { field: field.key, operator: props.operator, value: props.value };
  const shown = chipValues(ctx.entity, rule, ctx.options).map((v) => v.label);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-label={`${field.label} value`}
          disabled={ctx.disabled}
          className={cn(
            'h-8 max-w-[260px] justify-start',
            !shown.length && 'text-muted-foreground',
          )}
        >
          <span className="truncate">{shown.length ? shown.join(', ') : 'Choose…'}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto min-w-[240px] p-0" align="start">
        <RuleValueEditor
          dataset={ctx.entity}
          field={field}
          operator={props.operator}
          value={props.value}
          options={ctx.options}
          onChange={props.handleOnChange}
          onSubmit={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}

export function AddPill({ label, handleOnClick, title, context }: ActionProps) {
  const ctx = ctxOf(context);
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-7 gap-1 rounded-full px-2.5 text-xs"
      aria-label={title ?? String(label)}
      disabled={ctx.disabled}
      onClick={(e) => handleOnClick(e)}
    >
      <Plus className="size-3.5" aria-hidden />
      {label}
    </Button>
  );
}

export function RemoveX({ handleOnClick, title, context }: ActionProps) {
  const ctx = ctxOf(context);
  if (ctx.disabled) return null;
  // A field or relation card's row keeps its last rule; the row's own remove button takes it away.
  if (ctx.mode !== 'custom' && ctx.rootRules <= 1) return null;
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="ml-auto size-7 text-muted-foreground"
      aria-label={title ?? 'Remove'}
      onClick={(e) => handleOnClick(e)}
    >
      <X className="size-4" aria-hidden />
    </Button>
  );
}

/** "all of these" / "any of these" as a small word select. */
export function CombinatorWord({ value, handleOnChange, context }: CombinatorSelectorProps) {
  const ctx = ctxOf(context);
  return (
    <label className="flex items-center gap-1 text-xs text-muted-foreground">
      Match
      <select
        aria-label="Match"
        value={value}
        disabled={ctx.disabled}
        onChange={(e) => handleOnChange(e.target.value)}
        className="h-7 rounded-md border-none bg-transparent px-1 text-xs text-foreground"
      >
        <option value="and">all of these</option>
        <option value="or">any of these</option>
      </select>
    </label>
  );
}

/** "Not" on a group: the group matches when its conditions do not; shown so a negation never reads as positive. */
export function NotToggle({ checked, handleOnChange, context }: NotToggleProps) {
  const ctx = ctxOf(context);
  return (
    <label className="flex items-center gap-1 text-xs text-muted-foreground">
      <input
        type="checkbox"
        aria-label="Not"
        checked={Boolean(checked)}
        disabled={ctx.disabled}
        onChange={(e) => handleOnChange(e.target.checked)}
        className="size-3.5"
      />
      Not
    </label>
  );
}
